import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react'; // Import Loader2 icon

interface DropdownFieldProps {
  id: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: { value: string | undefined; label: string | React.ReactNode }[];
  disabled?: boolean;
  placeholder?: string;
  isLoading?: boolean; // Add isLoading prop
}

const DropdownField: React.FC<DropdownFieldProps> = ({
  id,
  value,
  onChange,
  options,
  disabled,
  placeholder,
  isLoading = false // Default to false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectedOption = options.find(opt => opt.value === value);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (optionValue: string | undefined) => {
    const e = {
      target: {
        id,
        value: optionValue,
        type: 'select'
      }
    } as unknown as React.ChangeEvent<HTMLSelectElement>;
    
    onChange(e);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className={`relative h-10 w-full rounded border border-slate-200 bg-white dark:bg-slate-800 
          dark:border-slate-600 px-4 pr-10 cursor-pointer flex items-center
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        onClick={() => !disabled && !isLoading && setIsOpen(!isOpen)}
      >
        <div className="flex-grow truncate text-sm text-slate-500 dark:text-slate-300">
          {selectedOption ? selectedOption.label : placeholder}
        </div>
        {isLoading ? (
          <Loader2 className="absolute right-2 h-5 w-5 text-slate-400 dark:text-slate-500 animate-spin" />
        ) : (
          <ChevronDown className={`absolute right-2 h-5 w-5 text-slate-400 dark:text-slate-500 transition-transform
            ${isOpen ? 'transform rotate-180' : ''}`} 
          />
        )}
      </div>

      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 
          dark:border-slate-600 rounded shadow-lg max-h-60 overflow-auto">
          {placeholder && (
            <div
              className="px-4 py-2 text-sm text-slate-500 dark:text-slate-300 hover:bg-secondary/10 
                hover:text-secondary cursor-pointer"
              onClick={() => handleSelect(undefined)}
            >
              {placeholder}
            </div>
          )}
          {options.map((option, index) => (
            <div
              key={option.value || index}
              className="px-4 py-2 text-sm text-slate-500 dark:text-slate-300 hover:bg-secondary/10 
                hover:text-secondary cursor-pointer"
              onClick={() => handleSelect(option.value)}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}

      {/* Hidden select for form submission */}
      <select
        id={id}
        value={value}
        onChange={onChange}
        className="hidden"
        disabled={disabled}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {/* Don't try to convert ReactNode to string */}
            {option.value}
          </option>
        ))}
      </select>
    </div>
  );
};

export default DropdownField;